/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, Button, Grid, TextField } from "@mui/material";

import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useEffect, useRef, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import Config from "../../Utils/Config";
import { DeviceModel, LocationModel, BrandModel, ModelModel } from "../../Utils/Models";
import { Vocabulary } from "../../Utils/Vocabulary";
import AddOrUpdateModel from "../GenericComponents/AddOrUpdateModel";
import { onChangeDatePicker, onChangeTextField } from "../../Utils/Utils";
import moment from "moment";
import AddAndShowImages from "../GenericComponents/AddAndShowImages";
import { toast } from "react-toastify";
import { getData } from "../../Services/getData";
import { urlEnum } from "../../Utils/UrlEnum";

type DeviceProps = {
  editDevice: DeviceModel | null;
  open: boolean;
  addOrUpdateDevice: (device: DeviceModel, files: any[]) => void;
  onClose: () => void;
};
export default function Device(props: DeviceProps) {
  const { editDevice, open, addOrUpdateDevice, onClose } = props;
  const [device, setDevice] = useState<DeviceModel>(new DeviceModel());
  const [pressOnSubmit, setPressOnSubmit] = useState(false);
  const [files, setFiles] = useState<any>([]);
  const [locations, setLocations] = useState<Array<LocationModel>>([]);
  const [brands, setBrands] = useState<Array<BrandModel>>([]);
  const [models, setModels] = useState<Array<ModelModel>>([]);
  const ref: any = useRef();
  const buttons = (
    <Button
      onClick={resetDevice}
      variant="contained"
      style={{ marginBottom: 10, marginTop: 10, color: "white" }}
      fullWidth
      color="secondary"
    >
      {Vocabulary.reset}
    </Button>
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locationsData, brandsData, modelsData] = await Promise.all([
          getData(urlEnum.locations),
          getData(urlEnum.brands),
          getData(urlEnum.deviceModels),
        ]);
        setLocations(locationsData.data.result);
        setBrands(brandsData.data.result);
        setModels(modelsData.data.result);
      } catch (error) {
        toast.error(`${error}`);
      }
    };
    fetchData();
  }, []);
  /**
   *
   */
  useEffect(() => {
    if (editDevice) {
      setDevice(editDevice);
    } else {
      setDevice(new DeviceModel());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDevice]);

  useEffect(() => {
    if (pressOnSubmit) {
      ref.current.submit();
      //reset the value
      handleChangePressOnSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressOnSubmit]);

  /**
   *
   */
  function handleChangePressOnSubmit() {
    setPressOnSubmit(!pressOnSubmit);
  }
  /**
   *
   */
  function resetDevice() {
    if (editDevice) {
      setDevice(editDevice);
    } else {
      setDevice(new DeviceModel());
    }
    setFiles([]);
  }

  /**
   * Handle image file changes
   */
  function handleChangeFiles(newFiles: any) {
    setFiles([...files, ...newFiles]);
  }

  /**
   * Handle image deletion
   */
  function handleDeleteImages(newImageList: any, newFileList: any) {
    setDevice({ ...device, images: newImageList.map((img: any) => img.path) });
    setFiles(newFileList);
  }

  /**
   * Display dropzone images
   */
  function displayDropzoneImages(images: any) {
    setDevice({ ...device, images });
  }

  return (
    <ValidatorForm ref={ref} onSubmit={() => addOrUpdateDevice(device, files)}>
      <AddOrUpdateModel
        open={open}
        title={Vocabulary.device}
        onClose={onClose}
        isSubmit={true}
        addOrUpdateItem={() => null}
        handleChangePressOnSubmit={handleChangePressOnSubmit}
        buttons={buttons ? buttons : null}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="serialNumber"
              id="serialNumber"
              label={Vocabulary.serialNumber}
              fullWidth
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
              value={device.serialNumber}
              onChange={(event) => setDevice(onChangeTextField(event, device))}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="name"
              id="name"
              label={Vocabulary.name}
              fullWidth
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
              value={device.name}
              onChange={(event) => setDevice(onChangeTextField(event, device))}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="code"
              id="code"
              label={Vocabulary.code}
              fullWidth
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
              value={device.code}
              onChange={(event) => setDevice(onChangeTextField(event, device))}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Autocomplete
              size="small"
              id="brand"
              freeSolo={false}
              disablePortal
              getOptionLabel={(option: any) => option.name || ""}
              isOptionEqualToValue={(option: any, value: any) =>
                option._id === value._id
              }
              options={brands}
              value={device.brand}
              onChange={(event: any, value: any) => {
                setDevice({ ...device, brand: value });
              }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="brand"
                  label={Vocabulary.brand}
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Autocomplete
              size="small"
              id="model"
              freeSolo={false}
              disablePortal
              getOptionLabel={(option: any) => option.name || ""}
              isOptionEqualToValue={(option: any, value: any) =>
                option._id === value._id
              }
              options={models}
              value={device.model}
              onChange={(event: any, value: any) => {
                setDevice({ ...device, model: value });
              }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="model"
                  label={Vocabulary.model}
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                name="purchaseDate"
                label={Vocabulary.purchaseDate}
                slotProps={{
                  textField: { style: { width: "100%" }, size: "small" },
                }}
                value={
                  typeof device.purchaseDate === "string"
                    ? moment(device.purchaseDate)
                    : device.purchaseDate
                }
                onChange={(e: any) =>
                  setDevice(onChangeDatePicker(e, device, "purchaseDate"))
                }
                format={Config.momentEUDateFormat}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              id="location"
              freeSolo={false}
              disablePortal
              getOptionLabel={(option: any) => `${option.name}`}
              isOptionEqualToValue={(option: any, value: any) =>
                option._id === value._id
              }
              options={locations}
              value={device.location}
              onChange={(event: any, value: any) => {
                setDevice({ ...device, location: value });
              }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="location"
                  label={Vocabulary.location || "Location"}
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <AddAndShowImages
              images={device.images}
              files={files}
              handleDeleteImages={handleDeleteImages}
              handleChangeFiles={handleChangeFiles}
              displayDropzoneImages={displayDropzoneImages}
            />
          </Grid>
        </Grid>
      </AddOrUpdateModel>
    </ValidatorForm>
  );
}
